import flatten from 'lodash/flatten';
import { observable, action, computed, makeObservable } from 'mobx';

import { DictionaryParams, Optional } from 'chaika-frontend-types/frontend-cms';
import { Locale, DictionaryNames } from 'chaika-enums';

import { DictionaryList } from './DictionaryList';

export class Dictionary {
    public static Name = 'dictionary' as const;

    @observable private dictionaries: Map<DictionaryNames | string, DictionaryList> = new Map();
    @observable private translatedDictionaries: Map<Locale, Map<DictionaryNames | string, DictionaryList>> = new Map();

    constructor() {
        makeObservable(this);
    }

    @computed
    public get names(): string[] {
        return Array.from(this.dictionaries.keys());
    }

    @computed
    public get loadedIds(): string[] {
        return flatten(Array.from(this.dictionaries.values()).map(({ ids }) => ids));
    }

    @action
    public getDictionary(name: DictionaryNames | string): DictionaryList {
        return this.dictionaries.get(name) || new DictionaryList();
    }

    @action
    public getTranslatedDictionaryList(locale: Locale): Optional<Map<string, Optional<DictionaryList>>> {
        return this.translatedDictionaries.get(locale);
    }

    @action
    public getTranslatedDictionary(locale: Locale, name: DictionaryNames | string): Optional<DictionaryList> {
        return this.translatedDictionaries.get(locale)?.get(name);
    }

    @action
    public getTranslationLoadedIds(locale: Locale): string[] {
        const dictionaries = this.translatedDictionaries.get(locale);
        return flatten(Array.from(dictionaries?.values() ?? []).map(({ ids }) => ids));
    }

    @action
    public addDictionaryItems(dictionaryName: DictionaryNames | string, items: DictionaryParams[] = []): void {
        const dictionary = this.dictionaries.get(dictionaryName);

        if (!dictionary) {
            const newDictionary = this.createDictionary(items);
            this.dictionaries.set(dictionaryName, newDictionary);
        } else {
            dictionary.add(items);
        }
    }

    @action
    public addTranslatedDictionaryItems(
        locale: Locale,
        dictionaryName: DictionaryNames | string,
        items: DictionaryParams[] = [],
    ): void {
        const hasDictionaryForLocale = this.translatedDictionaries.has(locale);

        if (!hasDictionaryForLocale) {
            this.translatedDictionaries.set(locale, new Map<DictionaryNames | string, DictionaryList>());
        }

        const dictionaries = this.translatedDictionaries.get(locale);
        const dictionary = dictionaries?.get(dictionaryName);

        if (!dictionary) {
            const newDictionary = this.createDictionary(items);
            dictionaries?.set(dictionaryName, newDictionary);
        } else {
            dictionary.add(items);
        }
    }

    @action
    private createDictionary(params: DictionaryParams[]): DictionaryList {
        const dictionary = new DictionaryList();
        dictionary.set(params);

        return dictionary;
    }
}

export const dictionary = new Dictionary();
